import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { isAccountPaused } from "@/config/adminAccountOptions";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "@/components/layout/Sidebar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import Bugsnag from "@bugsnag/js";
import { PolicyAcceptanceScreen } from "@/components/PolicyAcceptanceScreen";
import { APP_CONFIG } from "@/config/app.config";
import { getCurrentSignInProvider } from "@/lib/oauthLinkConfirmation";
import { requiresPolicyAcceptance } from "@/lib/policyAcceptance";

/**
 * Session ids we've already reported for an unreadable sign-in provider —
 * module-scoped so the report fires once per session rather than once per
 * render/paint, and survives remounts of ProtectedRoute within that session.
 */
const reportedUnreadableProviderSessions = new Set<string>();

/**
 * The AE-206 acceptance gate depends on being able to read the sign-in
 * provider off the session; a provider that can never be determined means the
 * gate can silently stop firing with no signal. Report it once per session so
 * that failure mode is observable, but stay quiet in dev — the auth-bypass
 * persona synthesises `identities: []`, which would spam this on every local
 * run.
 */
function reportUnreadableSignInProvider(sessionUserId: string): void {
  if (import.meta.env.DEV) return;
  if (reportedUnreadableProviderSessions.has(sessionUserId)) return;
  reportedUnreadableProviderSessions.add(sessionUserId);
  Bugsnag.notify(new Error("Unable to determine sign-in provider for an active session"), (event) => {
    event.severity = "warning";
    event.context = "protectedRoute.unreadableSignInProvider";
  });
}

export const ProtectedRoute = ({ withNavigation = true }: { withNavigation?: boolean }) => {
  const {
    isAuthenticated,
    userData,
    loading,
    signOut,
    session,
    policyAcceptedThisSession,
    markPolicyAcceptedThisSession,
  } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const location = useLocation();

  // AE-206: reporting must happen as an effect, not during render. A render
  // React discards under concurrent rendering would otherwise still mutate
  // the module-level dedup set, permanently suppressing a report that was
  // never actually delivered. Declared before every early return below so
  // the hook order stays stable across renders (rules of hooks); it computes
  // its own condition from `session` rather than reusing anything the gate
  // below derives.
  useEffect(() => {
    const provider = getCurrentSignInProvider(session?.user ?? null);
    if (session && provider === null) {
      reportUnreadableSignInProvider(session.user.id);
    }
  }, [session]);

  /** Wait for initial getSession() (and URL hash / PKCE recovery) before sending unauthenticated users away — otherwise magic links lose `#access_token=…`. */
  if (loading) {
    return (
      <div
        className={cn(
          "flex flex-1 h-full w-full items-center justify-center bg-gradient-to-b from-[#126c74]/30",
          withNavigation && "md:pl-64"
        )}
      >
        <Loader2 className="h-10 w-10 animate-spin text-white" aria-label="Loading" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const lastPath = location.pathname + location.search;
    localStorage.setItem("lastPath", lastPath);

    return <Navigate to="/login" replace />;
  }

  // AE-206: OAuth sessions never pass through the login screen's code step, so
  // this is where they accept the current Terms and Privacy Policy. It runs
  // before the onboarding redirect so a brand-new provider signup accepts
  // first, and it is gated on the provider so email/OTP sign-in is untouched.
  const signInProvider = getCurrentSignInProvider(session?.user ?? null);
  if (
    !policyAcceptedThisSession &&
    requiresPolicyAcceptance({
      provider: signInProvider,
      userData,
      currentTermsEffectiveDate: APP_CONFIG.CURRENT_TERMS_EFFECTIVE_DATE,
      currentPrivacyEffectiveDate: APP_CONFIG.CURRENT_PRIVACY_EFFECTIVE_DATE,
    })
  ) {
    return (
      <div
        className={cn(
          "flex h-full w-full flex-1 items-center justify-center bg-gradient-to-b from-[#126c74]/30 p-4",
          withNavigation && "md:pl-64"
        )}
      >
        <PolicyAcceptanceScreen
          userId={session?.user.id ?? ""}
          email={session?.user.email ?? ""}
          onAccepted={markPolicyAcceptedThisSession}
        />
      </div>
    );
  }

  if (withNavigation && !userData) {
    return <Navigate to="/complete-profile" replace />;
  }

  if (
    isAuthenticated &&
    withNavigation &&
    !profileLoading &&
    profile?.user_status &&
    isAccountPaused(profile.user_status)
  ) {
    return (
      <div
        className={cn(
          "flex flex-1 h-full w-full flex-col items-center justify-center gap-4 p-8 text-center bg-gradient-to-b from-[#126c74]/30",
          withNavigation && "md:pl-64"
        )}
      >
        <h1 className="text-xl font-semibold text-white">Account paused</h1>
        <p className="max-w-md text-sm text-white/80">
          Your Hey Bodhi access has been paused by an administrator. Contact your district
          leader or support if you believe this is a mistake.
        </p>
        <Button type="button" variant="secondary" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    );
  }

  if (
    isAuthenticated &&
    withNavigation &&
    !profileLoading &&
    profile &&
    !profile.is_super_admin &&
    !profile.district_gate_exempt &&
    profile.district_id === null
  ) {
    return (
      <div
        className={cn(
          "flex flex-1 h-full w-full flex-col items-center justify-center gap-4 p-8 text-center bg-gradient-to-b from-[#126c74]/30",
          withNavigation && "md:pl-64"
        )}
      >
        <h1 className="text-xl font-semibold text-white">Not available for your organization yet</h1>
        <p className="max-w-md text-sm text-white/80">
          HeyBodhi is currently available to customer school districts. If you believe this is
          an error, reach out to{" "}
          <a href="mailto:support@aieducators.ai" className="underline">
            support@aieducators.ai
          </a>{" "}
          and we'll get it sorted out.
        </p>
        <Button type="button" variant="secondary" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    );
  }

  return (
    <>
      {withNavigation && <Sidebar />}
      <main className={cn("flex min-h-0 flex-col flex-1 h-full w-full", withNavigation && "md:pl-64")}>
        <Outlet />
      </main>
    </>
  );
};
